/**
 * O mapa do relatório de risco no documento da mesa (E202.F2, GUS-145, ADR-0145 §7; ADR-0136 §2): o
 * texto do risco, o uso pela parte contrária, a resposta técnica e a providência de cada risco abrem
 * o editor do campo numa linha logo abaixo; o grau, o fato e a fonte seguem protegidos (vêm do exame).
 * "Tirar o risco" fica no campo do texto do risco; o relatório relido sai renumerado. Os riscos
 * tirados aparecem depois da tabela, com "Restaurar".
 *
 * Os campos são numerados pela composição sem edição (`r.<n>.<campo>`), e o papel renumera depois de
 * tirar um risco: a linha impressa `i` é o `i`-ésimo risco que não foi tirado.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { TrechoNaTelaData } from '@/lib/correcaoDoRascunho';
import type { GradeDoBlocoData, OrigemDoBlocoData } from '@/lib/documentoDaSaida';

import { GradeDoDocumento, type LinhaEditavel } from './GradeDoDocumento';
import { PALETA } from './paleta';
import { ParteEmEdicao } from './ParteEmEdicao';
import type { ContextoDasPartes } from './ParteDoDocumento';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa;

/** O campo de cada coluna do mapa (Nº, Grau, Risco, Fato e fonte, Uso, Resposta, Providência). */
const CAMPO_DA_COLUNA = [null, null, 'risco', null, 'uso', 'resposta', 'providencia'] as const;

const PREFIXO = 'r.';

/** Os números dos riscos na ordem da composição sem edição, e os tirados. */
export function riscosDoMapa(trechos: ReadonlyMap<string, TrechoNaTelaData>) {
  const numeros: number[] = [];
  for (const id of trechos.keys()) {
    if (!id.startsWith(PREFIXO) || !id.endsWith('.risco')) continue;
    numeros.push(Number(id.slice(PREFIXO.length, -'.risco'.length)));
  }
  const tirado = (n: number) => trechos.get(`${PREFIXO}${n}.risco`)?.no_laudo === null;
  return { impressos: numeros.filter((n) => !tirado(n)), tirados: numeros.filter(tirado) };
}

function EditorDoCampo({
  trecho,
  edicao,
}: {
  trecho: TrechoNaTelaData;
  edicao: EdicaoNoDocumento;
}): JSX.Element {
  return (
    <ParteEmEdicao
      key={trecho.id}
      trecho={trecho}
      paragrafos={[]}
      modo="simples"
      rascunho={edicao.rascunhos.get(trecho.id)}
      edicao={edicao}
    />
  );
}

function linhaDoRisco(
  numero: number | undefined,
  contexto: ContextoDasPartes,
  edicao: EdicaoNoDocumento,
): LinhaEditavel | null {
  if (numero === undefined) return null;
  const trechos = CAMPO_DA_COLUNA.map((campo) => {
    const id = campo !== null ? `${PREFIXO}${numero}.${campo}` : null;
    return id !== null && contexto.trechos.has(id) ? id : null;
  });
  const aberto = trechos.find((id) => id !== null && id === edicao.aberto) ?? null;
  const trecho = aberto !== null ? contexto.trechos.get(aberto) : undefined;
  return {
    trechos,
    abrir: (id) => edicao.abrir(id),
    depois: trecho !== undefined ? <EditorDoCampo trecho={trecho} edicao={edicao} /> : null,
  };
}

function Tirados({
  numeros,
  contexto,
  edicao,
}: {
  numeros: number[];
  contexto: ContextoDasPartes;
  edicao: EdicaoNoDocumento | null;
}) {
  if (numeros.length === 0) return null;
  return (
    <ul className="my-2 space-y-1 text-[0.9em]" data-testid="riscos-tirados">
      {numeros.map((n) => {
        const trecho = contexto.trechos.get(`${PREFIXO}${n}.risco`);
        if (trecho === undefined) return null;
        if (edicao !== null && edicao.aberto === trecho.id) {
          return (
            <li key={n}>
              <EditorDoCampo trecho={trecho} edicao={edicao} />
            </li>
          );
        }
        return (
          <li
            key={n}
            className="flex flex-wrap items-center gap-2 border-l-2 border-dashed pl-2 italic"
            style={{ borderColor: PALETA.linha, color: PALETA.mudo }}
          >
            {t.riscoTirado.replace('{texto}', trecho.base ?? '')}
            {edicao !== null ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="not-italic"
                onClick={() => edicao.abrir(trecho.id, 'restaurar')}
                data-testid="documento-restaurar-risco"
              >
                {t.edicao.restaurar}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function MapaDoRisco({
  grade,
  contexto,
  origem = null,
}: {
  grade: GradeDoBlocoData;
  contexto: ContextoDasPartes;
  /** E203.F2: a origem do mapa; cada risco abre o painel pela própria linha. */
  origem?: OrigemDoBlocoData | null;
}): JSX.Element {
  const { impressos, tirados } = riscosDoMapa(contexto.trechos);
  const { edicao } = contexto;
  const linhaEditavel =
    edicao !== null ? (i: number) => linhaDoRisco(impressos[i], contexto, edicao) : undefined;
  return (
    <div className="my-3" data-testid="mapa-do-risco">
      <GradeDoDocumento grade={grade} linhaEditavel={linhaEditavel} origem={origem} secao="mapa" />
      <Tirados numeros={tirados} contexto={contexto} edicao={edicao} />
    </div>
  );
}
